import { UserWithToken } from '$core/models/user';
import { environment } from '$env/environment';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '../auth';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { CookieHelperService } from './cookie-helper.service';
import { LanguageService } from './language.service';
import { UserAgreementUpdatingModalDismissState } from '$core/shell/user-agreement-updating-modal';

export class Credentials {
  public filial?: string;
  public login = '';
  public password = '';
  public type = '';
}

export interface AccessData {
  access_token: string;
  refresh_token?: string; // Not used
  expires_at: string;
  token_type: 'Bearer';
}

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService implements AuthService {
  private readonly userAgreementUpdatingModalDismissState = inject(
    UserAgreementUpdatingModalDismissState
  );

  public isAuthorized$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    !!localStorage.getItem('accessToken')
  );
  public authorizedSnapshot: boolean;
  private interruptedUrl: string;
  private readonly apiToken = '/v2/token';
  private readonly tokenType = 'Bearer';
  private readonly apiRefreshToken = '/v1/refresh-token';

  constructor(
    private http: HttpClient,
    private router: Router,
    private cookieHelperService: CookieHelperService,
    private languageService: LanguageService
  ) {
    this.isAuthorized$.subscribe((status) => {
      if (environment.saveTokenCookie) {
        this.cookieHelperService.setCookie(
          'accessToken',
          localStorage.getItem('accessToken'),
          {
            path: '/',
            domain: location.host
              .split('.')
              .reverse()
              .slice(0, 2)
              .reverse()
              .join('.'),
            'max-age': status ? 31536000 : -1,
          }
        );
      }

      this.authorizedSnapshot = status;
    });

    window.addEventListener(
      'storage',
      (e: StorageEvent) => {
        if (e.key === 'accessToken') {
          if (e.oldValue && !e.newValue) {
            this.logout();
          }

          if (!e.oldValue && e.newValue) {
            setTimeout(() => {
              this.isAuthorized$.next(true);
            }, 100);
          }
        }
      },
      false
    );
  }

  public isAuthorized(): Observable<boolean> {
    const isAuthorized: boolean = !!localStorage.getItem('accessToken');

    return of(isAuthorized);
  }

  public getAccessToken(): Observable<string> {
    const accessToken: string = localStorage.getItem('accessToken');

    return of(accessToken);
  }

  public getAccessTokenReactive(): Observable<string | null> {
    return this.isAuthorized$.pipe(
      map((isAuthorized) =>
        isAuthorized ? localStorage.getItem('accessToken') : null
      )
    );
  }

  public getAccessTokenWithType(): string {
    if (this.authorizedSnapshot) {
      return this.tokenType + ' ' + localStorage.getItem('accessToken');
    } else {
      return '';
    }
  }

  public refreshToken(): Observable<any> {
    return throwError('Not implemented');
  }

  public refreshShouldHappen(response: HttpErrorResponse): boolean {
    return false;
  }

  public verifyTokenRequest(url: string): boolean {
    return url.endsWith(this.apiRefreshToken);
  }

  public getInterruptedUrl(): string {
    return this.interruptedUrl;
  }

  public setInterruptedUrl(url: string): void {
    this.interruptedUrl = url;
  }

  /**
   * EXTRA AUTH METHODS
   */

  public login(credintials: Credentials): Observable<UserWithToken> {
    return this.http
      .post<UserWithToken>(this.apiToken, { ...credintials })
      .pipe(
        tap((res) => {
          this.saveAccessData(res.token);
          this.isAuthorized$.next(true);
        })
      );
  }

  /**
   * Logout
   */
  public async logout(notifyServer?: boolean) {
    if (notifyServer) {
      await this.http
        .post<{ status: string }>('/v1/users/logout', {})
        .toPromise();
    }
    this.clearAccessData();

    if (this.router.url.includes('/personal')) {
      await this.router.navigateByUrl(this.languageService.translateRoute('/'));
    }

    this.isAuthorized$.next(false);
  }

  /**
   * @description Update auth status manually
   * @param {AccessData} data
   */
  public update(data: AccessData, notify?: boolean): void {
    this.saveAccessData(data);
    if (notify) {
      this.isAuthorized$.next(true);
    }

    if (data) {
      this.authorizedSnapshot = true;
    }
  }

  /**
   * @description Save access data in the storage
   * @private
   * @param {AccessData} data
   */
  private saveAccessData({ access_token, refresh_token }: AccessData) {
    localStorage.setItem('accessToken', access_token);

    if (refresh_token) {
      localStorage.setItem('refreshToken', refresh_token);
    }
  }

  /**
   * @description Remove access data from storage
   * @private
   */
  public clearAccessData() {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    this.userAgreementUpdatingModalDismissState.clear();
  }
}
